/**
 * THE REASON, BEFORE THE RECORD.
 *
 * Canon 4.2, immutable rule 3: viewing identifiable personal data "requires a
 * short reason field BEFORE THE RECORD RENDERS — logged permanently in
 * admin_audit_log.reason_captured".
 *
 * "Before" is the entire specification. A dialogue that collects a reason,
 * fetches the record, renders it, and writes the log afterwards satisfies every
 * word except that one — and the difference is whether the log is a CONDITION
 * of the disclosure or a record that one happened. Only the first survives
 * somebody closing the tab halfway.
 *
 * So the log entry is written first, by fn_log_my_admin_action, which refuses a
 * blank reason, refuses an action the caller does not hold, and refuses an
 * export with no live time-boxed grant. Only if it returns an id does anything
 * get fetched. The refusals are the database's, in its own words.
 *
 * ---------------------------------------------------------------------------
 * TWO PRESENTATIONS, BECAUSE THERE ARE TWO WAYS TO ARRIVE HERE
 *
 * A MODAL, when somebody pressed "Open a record under this permission". They
 * asked for this; a dialogue over the page is the honest response, and Cancel
 * puts them back where they were.
 *
 * INLINE, when the gate stands in front of a section that is part of a card.
 * Those sections MOUNT WHEN THE CARD OPENS -- nobody pressed anything -- and a
 * fixed, full-viewport overlay thrown up on arrival is an ambush rather than a
 * question. Worse, it has nowhere to cancel to: dismissing it would render the
 * queue with no reason logged, which is the one thing Canon 4.2 forbids. So the
 * inline form has no Cancel and needs none. It sits where the queue would be,
 * the rest of the card is reachable around it, and the section simply waits.
 *
 * This was found in production, on the compliance card: SanctionsReview opened
 * the modal on mount and its Cancel was `() => setGate(true)` -- the flag that
 * renders it. DisputeQueue's was `() => {}`. Both trapped whoever opened the
 * card behind a full-screen dialogue whose only exit was to type a reason and
 * write it, permanently, to an append-only compliance log. THE ESCAPE HATCH WAS
 * FALSIFYING AN AUDIT RECORD, which is precisely the harm the reason gate
 * exists to prevent.
 *
 * InternalAudit had already solved it this way and said so in its own header --
 * "the clock renders first and the queue is opened deliberately". This makes
 * that the shared shape rather than one screen's good judgement.
 */

import { useState } from 'react';
import { O, SANS, Card, PrimaryCTA, QuietButton, Notice } from './ui';
import { logAdminAction } from './lib/portal';

// The database requires eight characters. Saying so up front is kinder than
// letting somebody type 'ok' and be refused by Postgres.
const MIN_REASON = 8;

export default function ReasonGate({ permission, onClose, onProceed, inline = false }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [logged, setLogged] = useState(null);

  const tooShort = reason.trim().length < MIN_REASON;

  async function proceed() {
    setError(null);
    setBusy(true);
    try {
      const res = await logAdminAction({ action: permission.permission_key, reason: reason.trim() });
      if (res.error) { setError(res.error); return; }
      setLogged(res.data);
      if (onProceed) onProceed(res.data);
    } finally {
      setBusy(false);
    }
  }

  // The heading's id is per permission, not a constant. A card may host two
  // gated sections -- the compliance card hosts both the sanctions queue and
  // the dispute queue -- and two inline gates sharing one DOM id makes both
  // aria-labelledby references point at whichever rendered first.
  const titleId = `reason-title-${permission.permission_key}`;

  const body = (
    <Card>
      <h2 id={titleId} style={{ fontSize: 15.5, fontWeight: 700, color: O.ink }}>
        Why are you opening this record?
      </h2>
      <p style={{ marginTop: 7, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
        <code style={{ color: O.ink }}>{permission.permission_key}</code> touches
        identifiable personal data. Your reason is written to the append-only
        activity log before anything is shown, and cannot be edited afterwards.
      </p>

      {!logged ? (
        <>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            aria-label="Reason"
            // ONLY THE MODAL TAKES FOCUS. A modal was asked for, so focusing
            // its field is helpful. An inline gate mounts when the card opens
            // and stealing focus there scrolls somebody to a section they did
            // not choose -- the same ambush in a smaller form.
            autoFocus={!inline}
            placeholder="responding to ticket #4821"
            style={{
              width: '100%', boxSizing: 'border-box', marginTop: 14, resize: 'none',
              padding: '12px 13px', borderRadius: 11, fontFamily: SANS, fontSize: 13.5,
              border: `1px solid ${O.hairlineOn}`, color: O.ink, background: '#FFFFFF',
            }}
          />
          {tooShort && reason.length > 0 && (
            <p style={{ marginTop: 6, fontSize: 11.5, color: O.inkFaint }}>
              A few more words — at least {MIN_REASON} characters.
            </p>
          )}

          {error && <div style={{ marginTop: 12 }}><Notice tone="critical">{error}</Notice></div>}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            {!inline && <QuietButton onClick={onClose}>Cancel</QuietButton>}
            <PrimaryCTA onClick={proceed} busy={busy} disabled={busy || tooShort}>
              Record and continue
            </PrimaryCTA>
          </div>
        </>
      ) : (
        <>
          <div style={{ marginTop: 14 }}>
            <Notice tone="info">
              Recorded. The record viewer for this permission is not built yet. The
              log entry stands regardless — it is not conditional on what happens
              next, which is the point of writing it first.
            </Notice>
          </div>
          {!inline && (
            <div style={{ marginTop: 14 }}>
              <QuietButton onClick={onClose}>Close</QuietButton>
            </div>
          )}
        </>
      )}
    </Card>
  );

  // INLINE: in the page, in the flow, no overlay and no Cancel. There is
  // nothing to cancel back to -- the card around it is already there.
  if (inline) return <div style={{ fontFamily: SANS }}>{body}</div>;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 20,
      background: 'rgba(13,36,24,0.45)', fontFamily: SANS,
    }}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} style={{ width: '100%', maxWidth: 440 }}>
        {body}
      </div>
    </div>
  );
}
